import React, { createContext, useCallback, useContext, useEffect, useId, useRef, useState } from 'react';
import { X } from 'lucide-react';

/**
 * Shared dialog system used by every modal in the app.
 *
 * Composition:
 *   <Dialog size="lg" onClose={fn} labelledBy="my-title">
 *     <DialogHeader id="my-title" title="Title" icon={<Icon/>} onClose={fn} />
 *     <form className="dlg-form" onSubmit={...}>          // forms only
 *       <DialogBody>...fields...</DialogBody>
 *       <DialogFooter>...buttons...</DialogFooter>
 *     </form>
 *   </Dialog>
 *
 * For non-form dialogs, put <DialogBody>/<DialogFooter> directly inside <Dialog>.
 * The shell gives a sticky header + scrollable body + sticky footer, responsive
 * sizing (sm/md/lg/xl) and body-scroll lock while open.
 *
 * Closing is always deliberate: the X, a Cancel/Close button, or Escape. A click
 * outside never closes a dialog -- it used to, and a text selection dragged out
 * of a field and released over the backdrop counted as one, which threw away
 * whatever had been typed. The dialog nudges instead, to show it is still open.
 *
 * Escape and the X ask "Discard your changes?" once anything has been typed,
 * picked or toggled inside the dialog. A dialog whose data does not arrive by
 * typing (an imported roster, a chosen file) passes `isDirty`, which then
 * decides alone; `confirmOnClose={false}` turns the question off, for
 * confirmations and read-only dialogs. Cancel buttons close without asking:
 * pressing Cancel is itself the choice to discard.
 *
 * Only the topmost dialog answers Escape and keeps keyboard focus, so one Escape
 * in the roster import cannot also close the form underneath it. Focus returns
 * to whatever opened the dialog when it closes.
 */

// Open dialogs, bottom to top.
const openDialogs = [];
let lockedOverflow = null;

/** True while any dialog is open. Page-level keyboard shortcuts should stand down. */
export function hasOpenDialog() {
  return openDialogs.length > 0;
}

const DialogContext = createContext(null);

const FOCUSABLE = [
  'a[href]', 'button:not([disabled])', 'input:not([disabled]):not([type="hidden"])',
  'select:not([disabled])', 'textarea:not([disabled])', '[tabindex]:not([tabindex="-1"])',
].join(', ');

function focusableIn(root) {
  return Array.from(root.querySelectorAll(FOCUSABLE))
    .filter((el) => !el.closest('[hidden], [inert]'));
}

function nudge(panel) {
  if (!panel || typeof panel.animate !== 'function') return;
  if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) return;
  panel.animate(
    [{ transform: 'scale(1)' }, { transform: 'scale(1.012)' }, { transform: 'scale(1)' }],
    { duration: 220, easing: 'ease-out' }
  );
}

export function Dialog({
  children, onClose, size = 'md', labelledBy, zIndex, width,
  isDirty, confirmOnClose = true, discardPrompt,
}) {
  const id = useId();
  const panelRef = useRef(null);
  // Captured during the first render, before any autoFocus inside moves focus.
  const [opener] = useState(() => document.activeElement);
  const [edited, setEdited] = useState(false);
  const [pendingClose, setPendingClose] = useState(null);
  const returnFocus = useRef(null);

  const dirty = confirmOnClose && (isDirty !== undefined ? Boolean(isDirty) : edited);
  const latest = useRef({});
  latest.current = { onClose, dirty, pendingClose };

  const requestClose = useCallback((close) => {
    const fn = close || latest.current.onClose;
    if (!fn) return;
    if (!latest.current.dirty) { fn(); return; }
    returnFocus.current = document.activeElement;
    setPendingClose(() => fn);
  }, []);

  const keepEditing = useCallback(() => {
    setPendingClose(null);
    const back = returnFocus.current;
    returnFocus.current = null;
    if (back && back.isConnected) back.focus();
    else panelRef.current?.focus();
  }, []);

  useEffect(() => {
    openDialogs.push(id);
    if (openDialogs.length === 1) {
      lockedOverflow = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
    }
    const panel = panelRef.current;
    if (panel && !panel.contains(document.activeElement)) panel.focus();
    return () => {
      const at = openDialogs.lastIndexOf(id);
      if (at !== -1) openDialogs.splice(at, 1);
      if (openDialogs.length === 0) {
        document.body.style.overflow = lockedOverflow || '';
        lockedOverflow = null;
      }
      if (opener && opener.isConnected && typeof opener.focus === 'function') opener.focus();
    };
  }, [id, opener]);

  useEffect(() => {
    const onKey = (e) => {
      if (openDialogs[openDialogs.length - 1] !== id) return;
      const panel = panelRef.current;
      if (!panel) return;
      if (e.key === 'Escape') {
        // An open menu or picker inside the dialog may have claimed it.
        if (e.defaultPrevented) return;
        e.stopPropagation();
        if (latest.current.pendingClose) keepEditing();
        else requestClose();
        return;
      }
      if (e.key !== 'Tab') return;
      const scope = panel.querySelector('.dlg-confirm') || panel;
      const nodes = focusableIn(scope);
      if (nodes.length === 0) { e.preventDefault(); scope.focus(); return; }
      const first = nodes[0];
      const last = nodes[nodes.length - 1];
      const active = document.activeElement;
      if (!scope.contains(active) || (e.shiftKey && (active === first || active === scope))) {
        e.preventDefault();
        (e.shiftKey ? last : first).focus();
      } else if (!e.shiftKey && active === last) {
        e.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [id, requestClose, keepEditing]);

  // Anything typed, picked or toggled inside counts as unsaved work.
  useEffect(() => {
    const panel = panelRef.current;
    if (!panel) return undefined;
    const mark = () => setEdited(true);
    panel.addEventListener('input', mark, true);
    panel.addEventListener('change', mark, true);
    return () => {
      panel.removeEventListener('input', mark, true);
      panel.removeEventListener('change', mark, true);
    };
  }, []);

  const discard = () => {
    const fn = pendingClose;
    setPendingClose(null);
    returnFocus.current = null;
    fn?.();
  };

  return (
    <DialogContext.Provider value={{ requestClose }}>
      <div
        className="dlg-overlay"
        onMouseDown={(e) => {
          if (e.target !== e.currentTarget) return;
          // Keep focus (and any caret) where it was; the press only nudges.
          e.preventDefault();
          nudge(panelRef.current);
        }}
        role="presentation"
        style={zIndex ? { zIndex } : undefined}
      >
        <div
          ref={panelRef}
          className={`dlg dlg-${size}`}
          style={width ? { maxWidth: width } : undefined}
          role="dialog"
          aria-modal="true"
          aria-labelledby={labelledBy}
          tabIndex={-1}
        >
          {children}
          {pendingClose && (
            <DiscardConfirm prompt={discardPrompt} onKeep={keepEditing} onDiscard={discard} />
          )}
        </div>
      </div>
    </DialogContext.Provider>
  );
}

function DiscardConfirm({ prompt = {}, onKeep, onDiscard }) {
  const keepRef = useRef(null);
  const titleId = useId();
  const bodyId = useId();
  useEffect(() => { keepRef.current?.focus(); }, []);
  return (
    <div className="dlg-confirm-layer">
      <div
        className="dlg-confirm"
        role="alertdialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={bodyId}
        tabIndex={-1}
      >
        <h3 id={titleId}>{prompt.title || 'Discard your changes?'}</h3>
        <p id={bodyId}>{prompt.body || 'What you entered in this window has not been saved.'}</p>
        <div className="dlg-confirm-actions">
          <button type="button" ref={keepRef} className="btn btn-secondary" onClick={onKeep}>
            {prompt.keep || 'Keep editing'}
          </button>
          <button type="button" className="btn btn-danger" onClick={onDiscard}>
            {prompt.discard || 'Discard'}
          </button>
        </div>
      </div>
    </div>
  );
}

export function DialogHeader({ title, icon, onClose, id, children }) {
  const dialog = useContext(DialogContext);
  const close = () => (dialog ? dialog.requestClose(onClose) : onClose());
  return (
    <div className="dlg-head">
      <h2 id={id}>
        {icon && <span className="dlg-head-icon">{icon}</span>}
        {title}
      </h2>
      {children}
      {onClose && (
        <button type="button" className="dlg-close" onClick={close} aria-label="Close dialog">
          <X size={20} aria-hidden="true" />
        </button>
      )}
    </div>
  );
}

export function DialogBody({ children, className = '', style }) {
  return <div className={`dlg-body ${className}`.trim()} style={style}>{children}</div>;
}

export function DialogFooter({ children, spread = false }) {
  return <div className={`dlg-foot${spread ? ' dlg-foot-spread' : ''}`}>{children}</div>;
}

/** Optional grouping with an uppercase section label and divider. */
export function FormSection({ title, children, style }) {
  return (
    <div className="form-section" style={style}>
      {title && <div className="form-section-title">{title}</div>}
      {children}
    </div>
  );
}

/** Labeled field wrapper: label on top, control as children, optional hint below. */
export function Field({ label, htmlFor, hint, required, children, className = '' }) {
  return (
    <div className={`field ${className}`.trim()}>
      {label && (
        <label className="field-label" htmlFor={htmlFor}>
          {label}{required && ' *'}
        </label>
      )}
      {children}
      {hint && <p className="field-hint">{hint}</p>}
    </div>
  );
}

/** Accessible on/off switch matching the dialog system. */
export function Switch({ checked, onChange, id, ariaLabel, disabled }) {
  return (
    <span className="switch" style={disabled ? { opacity: 0.5, cursor: 'not-allowed' } : undefined}>
      <input
        type="checkbox"
        id={id}
        checked={checked}
        onChange={onChange}
        disabled={disabled}
        aria-label={ariaLabel}
      />
      <span className="switch__track" aria-hidden="true" />
      <span className="switch__thumb" aria-hidden="true" />
    </span>
  );
}

/**
 * A full toggle row: title + description on the left, a switch on the right.
 * The whole row is a label so clicking anywhere toggles it.
 */
export function SwitchRow({ title, description, checked, onChange, id, disabled }) {
  return (
    <label className="switch-row" htmlFor={id} style={{ cursor: disabled ? 'not-allowed' : 'pointer' }}>
      <span className="switch-row__text">
        <span className="switch-row__title">{title}</span>
        {description && <span className="switch-row__desc" style={{ display: 'block' }}>{description}</span>}
      </span>
      <Switch id={id} checked={checked} onChange={onChange} ariaLabel={title} disabled={disabled} />
    </label>
  );
}
